import {useId} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'
import IconEuro from '@adv-mt/mt-svg-icons/lib/Euro.js'
import IconInfoOutline from '@adv-mt/mt-svg-icons/lib/InfoOutline.js'
import {Field} from '@adv-mt/ui/field'
import {Icon, ICON_INTENTS, ICON_SIZES} from '@adv-mt/ui/icon'
import {
  INPUT_GROUP_ADDON_ALIGNS,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText
} from '@adv-mt/ui/input-group'
import {Selector} from '@adv-mt/ui/selector'
import {Body1, Body2, Caption, Headline1} from '@adv-mt/ui/typography'

import {BASE_CLASS, ENTRY_MAX_LENGTH, FARE_DESCRIPTION_KEYS, FARE_TYPES, MONTHS_PER_YEAR} from '../config.js'
import {getEntryHelpKey} from '../utils.js'
import FareOption from './FareOption.js'

// Reads as `84 meses (7 años)`.
const formatTermsLabel = ({i18n, terms}) =>
  `${i18n.t('COMMON_N_MONTHS', terms)} (${i18n.t('COMMON_N_YEARS', terms / MONTHS_PER_YEAR)})`

const FinancingCalculator = ({
  appliedEntry,
  cashPrice,
  currentFare = {},
  entryError,
  entryFieldKey,
  entryValue,
  fareLabelKey,
  fares,
  financedPrice,
  hasCommittedEntry,
  onEntryCommit,
  onEntryChange,
  onFareSelect,
  onInsuranceInfoClick,
  onTermsChange,
  selectedFare,
  selectedTerms,
  showsFareSelector
}) => {
  const {i18n} = useSuiContext()
  const id = useId()

  const {availableTerms = []} = fares[0] ?? {}
  // The accepted range belongs to the fare being quoted, which is the same one the entry is validated
  // against. Reading it off `fares[0]` would let the input advertise a range the validation rejects.
  const {maxEntry, minEntry} = currentFare
  const selectedTermsOnly = selectedTerms != null ? [selectedTerms] : []
  const termOptions = availableTerms.length > 0 ? availableTerms : selectedTermsOnly

  // The trigger shows a label only for the values listed here, so the options are built from the same
  // map. Without it the trigger falls back to the raw value and reads `84` instead of `84 meses (7 años)`.
  const termLabelsByValue = Object.fromEntries(
    termOptions.map(terms => [String(terms), formatTermsLabel({i18n, terms})])
  )

  // Reads off the settled entry, not the one being typed, so the text appears when the user commits a
  // value and does not flicker keystroke by keystroke. The error takes the same slot when there is one.
  const entryHelpKey =
    entryError || !hasCommittedEntry
      ? null
      : getEntryHelpKey({
          cashPrice,
          entry: appliedEntry,
          financedPrice,
          maxEntryToGetDiscount: currentFare.maxEntryToGetDiscount,
          selectedOption: currentFare.selectedOption
        })

  const entryDescribedBy = entryError ? `${id}-entryError` : entryHelpKey && `${id}-entryHelp`

  return (
    <>
      <Field.Root>
        <Field.Label htmlFor={`${id}-entry`} className={`${BASE_CLASS}-entryLabel`}>
          {i18n.t(entryFieldKey)}
        </Field.Label>
        <InputGroup>
          <InputGroupInput
            aria-describedby={entryDescribedBy || undefined}
            aria-invalid={entryError ? true : undefined}
            id={`${id}-entry`}
            inputMode="decimal"
            max={maxEntry}
            maxLength={ENTRY_MAX_LENGTH}
            min={minEntry}
            onBlur={onEntryCommit}
            onChange={event => onEntryChange(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && onEntryCommit()}
            placeholder={minEntry}
            type="number"
            value={entryValue}
          />
          <InputGroupAddon align={INPUT_GROUP_ADDON_ALIGNS.INLINE_END}>
            <InputGroupText>
              <Icon color={ICON_INTENTS.PRIMARY} size={ICON_SIZES.MD}>
                <IconEuro color="currentColor" size={ICON_SIZES.MD} />
              </Icon>
            </InputGroupText>
          </InputGroupAddon>
        </InputGroup>
        {entryHelpKey && <Field.Description id={`${id}-entryHelp`}>{i18n.t(entryHelpKey)}</Field.Description>}
        {entryError && (
          <Field.Error id={`${id}-entryError`} match role="alert" icon={<IconClose />}>
            {i18n.t(entryError.key, {amount: i18n.c(entryError.amount)})}
          </Field.Error>
        )}
      </Field.Root>

      <Field.Root>
        <Field.Label htmlFor={`${id}-terms`} id={`${id}-termsLabel`} className={`${BASE_CLASS}-entryLabel`}>
          {i18n.t('COMMON.TERMS')}
        </Field.Label>
        <Selector.Root
          items={termLabelsByValue}
          onValueChange={onTermsChange}
          value={selectedTerms != null ? String(selectedTerms) : null}
        >
          <Selector.Trigger aria-labelledby={`${id}-termsLabel`} id={`${id}-terms`} />
          <Selector.Popup>
            {Object.entries(termLabelsByValue).map(([value, label]) => (
              <Selector.Item key={value} value={value}>
                {label}
              </Selector.Item>
            ))}
          </Selector.Popup>
        </Selector.Root>
      </Field.Root>

      {showsFareSelector ? (
        <fieldset className={`${BASE_CLASS}-fareSection`}>
          <legend className={`${BASE_CLASS}-fareLegend`}>
            <Body1 highlight>{i18n.t(fareLabelKey)}</Body1>
          </legend>
          <div className={`${BASE_CLASS}-fareOptions`}>
            {fares.map(({fareType, fee, insuranceName}) => (
              <FareOption
                description={i18n.t(FARE_DESCRIPTION_KEYS[fareType], {insuranceName})}
                fee={fee}
                id={`${id}-fare-${fareType}`}
                isSelected={fareType === selectedFare}
                key={fareType}
                name={`${id}-fare`}
                onSelect={() => onFareSelect(fareType)}
                value={fareType}
              >
                {fareType === FARE_TYPES.WITH_INSURANCE && (
                  <button
                    className={`${BASE_CLASS}-insuranceInfoLink`}
                    onClick={onInsuranceInfoClick}
                    tabIndex={fareType === selectedFare ? 0 : -1}
                    type="button"
                  >
                    <Icon size={ICON_SIZES.SM}>
                      <IconInfoOutline size={ICON_SIZES.SM} />
                    </Icon>
                    <Caption>{i18n.t('PANEL_FINANCING.B2C.FARE_SELECTOR_LINK')}</Caption>
                  </button>
                )}
              </FareOption>
            ))}
          </div>
        </fieldset>
      ) : (
        <div className={`${BASE_CLASS}-fareSection ${BASE_CLASS}-fareSection--inline`}>
          <Body1 highlight>{i18n.t(fareLabelKey)}</Body1>
          {currentFare.fee != null && (
            <span className={`${BASE_CLASS}-fareAmount`}>
              <Headline1 as="span">{i18n.c(currentFare.fee)}</Headline1>
              <Body2 highlight>/{i18n.t('COMMON.PLURALS.MONTH', 1).toLowerCase()}*</Body2>
            </span>
          )}
        </div>
      )}
    </>
  )
}

FinancingCalculator.displayName = 'FinancingCalculator'

FinancingCalculator.propTypes = {
  appliedEntry: PropTypes.number,
  cashPrice: PropTypes.number,
  currentFare: PropTypes.object,
  entryError: PropTypes.shape({amount: PropTypes.number, key: PropTypes.string}),
  entryFieldKey: PropTypes.string.isRequired,
  entryValue: PropTypes.string.isRequired,
  fareLabelKey: PropTypes.string.isRequired,
  fares: PropTypes.arrayOf(PropTypes.object).isRequired,
  financedPrice: PropTypes.number,
  hasCommittedEntry: PropTypes.bool,
  onEntryCommit: PropTypes.func.isRequired,
  onEntryChange: PropTypes.func.isRequired,
  onFareSelect: PropTypes.func.isRequired,
  onInsuranceInfoClick: PropTypes.func.isRequired,
  onTermsChange: PropTypes.func.isRequired,
  selectedFare: PropTypes.string.isRequired,
  selectedTerms: PropTypes.number,
  showsFareSelector: PropTypes.bool.isRequired
}

export default FinancingCalculator
